import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CustomerHistory = ({ customerData }) => {
  const [activeTab, setActiveTab] = useState('transactions');

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'rejected': return 'text-error bg-error/10';
      case 'pending': return 'text-warning bg-warning/10';
      case 'processing': return 'text-primary bg-primary/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'low': return 'text-success';
      case 'medium': return 'text-warning';
      case 'high': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const tabs = [
    { id: 'transactions', label: 'Transaction History', icon: 'History' },
    { id: 'profile', label: 'Customer Profile', icon: 'User' },
    { id: 'compliance', label: 'Compliance Records', icon: 'Shield' },
    { id: 'notes', label: 'Review Notes', icon: 'FileText' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Customer Analysis</h2>
        <div className="flex items-center space-x-2">
          <Icon name="TrendingUp" size={20} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Risk Profile: 
            <span className={`ml-1 font-medium ${getRiskLevelColor(customerData?.riskProfile)}`}>
              {customerData?.riskProfile}
            </span>
          </span>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-muted/30 p-1 rounded-lg">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 flex-1 justify-center ${
              activeTab === tab?.id
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div className="space-y-4">
        {activeTab === 'transactions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-foreground">Recent Transactions</h3>
              <span className="text-sm text-muted-foreground">
                {customerData?.transactionHistory?.length} total transactions
              </span>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {customerData?.transactionHistory?.map((transaction) => (
                <div
                  key={transaction?.id}
                  className="border border-border rounded-lg p-4 hover:bg-muted/20 transition-colors duration-150"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="ArrowRightLeft" size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {formatCurrency(transaction?.amount, transaction?.currency)}
                        </p>
                        <p className="text-sm text-muted-foreground">to {transaction?.recipient}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(transaction?.status)}`}>
                        {transaction?.status}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(transaction?.date)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Risk Score:</span>
                      <span className="text-foreground">{transaction?.riskScore}/100</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Country:</span>
                      <span className="text-foreground">{transaction?.country}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div>
              <h3 className="font-medium text-foreground mb-4">Customer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Full Name:</span>
                    <span className="text-sm text-foreground font-medium">{customerData?.profile?.fullName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <span className="text-sm text-foreground">{customerData?.profile?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Phone:</span>
                    <span className="text-sm text-foreground">{customerData?.profile?.phone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Date of Birth:</span>
                    <span className="text-sm text-foreground">{formatDate(customerData?.profile?.dateOfBirth)}</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Nationality:</span>
                    <span className="text-sm text-foreground">{customerData?.profile?.nationality}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Occupation:</span>
                    <span className="text-sm text-foreground">{customerData?.profile?.occupation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Customer Since:</span>
                    <span className="text-sm text-foreground">{formatDate(customerData?.profile?.customerSince)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">KYC Status:</span>
                    <span className={`text-sm px-2 py-1 rounded-full font-medium ${
                      customerData?.profile?.kycStatus === 'verified' 
                        ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}>
                      {customerData?.profile?.kycStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-foreground mb-4">Account Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/30 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{customerData?.statistics?.totalTransactions}</p>
                  <p className="text-sm text-muted-foreground">Total Transactions</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(customerData?.statistics?.totalVolume, 'USD')}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Volume</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{customerData?.statistics?.avgRiskScore}</p>
                  <p className="text-sm text-muted-foreground">Avg Risk Score</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'compliance' && (
          <div className="space-y-4">
            <h3 className="font-medium text-foreground">Compliance History</h3>
            
            <div className="space-y-3">
              {customerData?.complianceRecords?.map((record) => (
                <div
                  key={record?.id}
                  className="border border-border rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="Shield" size={16} className="text-primary" />
                      <span className="font-medium text-foreground">{record?.type}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(record?.status)}`}>
                      {record?.status}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2">{record?.description}</p>
                  
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Performed by: {record?.performedBy}</span>
                    <span className="text-muted-foreground">{formatDate(record?.date)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-foreground">Review Notes</h3>
              <Button
                size="sm"
                variant="outline"
                iconName="Plus"
                iconPosition="left"
                iconSize={14}
              >
                Add Note
              </Button>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {customerData?.reviewNotes?.map((note) => (
                <div
                  key={note?.id}
                  className="border border-border rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center">
                        <Icon name="User" size={12} color="white" />
                      </div>
                      <span className="font-medium text-foreground">{note?.author}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(note?.date)}</span>
                  </div>
                  
                  <p className="text-sm text-foreground">{note?.content}</p>
                  
                  {note?.category && (
                    <div className="mt-2">
                      <span className="text-xs px-2 py-1 bg-muted/50 text-muted-foreground rounded-full">
                        {note?.category}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerHistory;